/** @jsxImportSource @emotion/react */

import React, {
  Fragment,
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { CSSObject } from '@emotion/react';
import useNavigation from '../hooks/useNavigation';
import lazyWithChunkErrorFallback from '../utils/lazyWithChunkErrorFallback';
import parseUri from '../utils/parseUri';
import type { IScreenSource } from '../contexts/screenContext';
import type { ISearchSource } from '../contexts/searchContext';
import type { IFlexSource } from '../contexts/flexContext';
import Loader from '../components/loader';
import { cancelAllRequests } from '../utils/request';
import type { ILoginSource } from '../contexts/loginContext';
import type { OnfidoContext } from '../contexts/onfidoContext';
import useInternalUrl from '../hooks/useInternalUrl';
import layout from '../constants/layout';
import useTheme from '../hooks/useTheme';
import currentPartner from '../constants/partners';
import { ViewContext } from '../contexts/viewContext';

const FlexView = React.lazy(
  lazyWithChunkErrorFallback(
    () => import(/* webpackChunkName: "view-screen" */ './flex/flexView')
  )
);
const ScreenView = React.lazy(
  lazyWithChunkErrorFallback(
    () => import(/* webpackChunkName: "view-screen" */ './screen/screenView')
  )
);
const SearchScreenView = React.lazy(
  lazyWithChunkErrorFallback(
    () =>
      import(/* webpackChunkName: "view-search" */ './screen/searchScreenView')
  )
);
const OnfidoProvider = React.lazy(
  lazyWithChunkErrorFallback(() =>
    import(
      /* webpackChunkName: "view-onfido" */ '../contexts/onfidoContext'
    ).then((m) => ({ default: m.OnfidoProvider }))
  )
);
const DebugContextProvider = React.lazy(
  lazyWithChunkErrorFallback(() =>
    import(
      /* webpackChunkName: "view-debug" */ '../contexts/debugContext'
    ).then((m) => ({ default: m.DebugContextProvider }))
  )
);
const LoginContextProvider = React.lazy(
  lazyWithChunkErrorFallback(() =>
    import(
      /* webpackChunkName: "view-login" */ '../contexts/loginContext'
    ).then((m) => ({ default: m.LoginContextProvider }))
  )
);
const LanguageSelectorContextProvider = React.lazy(
  lazyWithChunkErrorFallback(() =>
    import(
      /* webpackChunkName: "view-language" */ '../contexts/languageSelectorContext'
    ).then((m) => ({ default: m.LanguageSelectorContextProvider }))
  )
);

interface IBaseView {
  title?: string;
  hideAppMenu?: boolean;
  preventBack?: boolean;

  constrainHeight?: number;
  autoHeight?: boolean;

  isTabRoot?: boolean;
  isModalRoot?: boolean;
  isModalDescendant?: boolean;
  isSpringDetails?: boolean;
}

interface IScreenView extends IBaseView {
  type: 'SCREEN';
  source: IScreenSource;
}

interface ISearchScreenView extends IBaseView {
  type: 'SEARCH_SCREEN';
  source: ISearchSource;
}

interface IFlexView extends IBaseView {
  type: 'FLEX';
  source: IFlexSource;
}

interface ILoginView extends IBaseView {
  type: 'LOGIN';
  source?: ILoginSource;
}

interface ILanguageSelectorView extends IBaseView {
  type: 'LANGUAGE_SELECTOR';
}

interface IDebugView extends IBaseView {
  type: 'DEBUG';
}

interface IOnfidoView extends IBaseView {
  type: 'ONFIDO';
  source: OnfidoContext;
}

export type IView =
  | IScreenView
  | ISearchScreenView
  | IFlexView
  | IDebugView
  | ILoginView
  | IOnfidoView
  | ILanguageSelectorView;

interface IViewProps {
  view: IView;
}

const View = React.memo(({ view }: IViewProps) => {
  const { setHeaderElement, setMainElement, setFooterElement } =
    useContext(ViewContext);
  const [componentHasRendered, setComponentHasRendered] = useState(false);

  const { color } = useTheme();
  const {
    getCurrentView,
    getPostNavigationOptions,
    clearPostNavigationOptions,
    getLocationKey,
    redirectToPath,
  } = useNavigation();
  const handleInternalUrl = useInternalUrl();

  const headerElRef = useRef<HTMLElement | null>();
  const mainElRef = useRef<HTMLElement | null>();
  const footerElRef = useRef<HTMLElement | null>();
  const currentView = getCurrentView();

  const getPartnerHeaderHeight = () => {
    if (
      !!currentPartner &&
      !(
        currentPartner.hideKryLiviLogoSection === true &&
        currentPartner.logo == null
      )
    ) {
      return layout.PARTNER_HEADER_HEIGHT;
    }
    return 0;
  };

  const getViewComponent = () => {
    switch (view.type) {
      case 'ONFIDO':
        return <OnfidoProvider routeSource={view.source} />;
      case 'SCREEN':
        return <ScreenView routeSource={view.source} />;
      case 'SEARCH_SCREEN':
        return (
          <SearchScreenView
            routeSource={view.source}
            locationKey={getLocationKey()}
          />
        );
      case 'FLEX':
        return <FlexView routeSource={view.source} />;
      case 'DEBUG':
        return <DebugContextProvider />;
      case 'LOGIN':
        return <LoginContextProvider routeSource={view.source} />;
      case 'LANGUAGE_SELECTOR':
        return <LanguageSelectorContextProvider />;
      default:
        return null;
    }
  };

  // Cancel all ongoing requests upon navigation.
  // @TODO: Look into tagging requests to be able to only cancel the ones
  // that it might apply to.
  useLayoutEffect(() => {
    const { persistRequests } = getPostNavigationOptions();

    if (!persistRequests) {
      cancelAllRequests();
    }
  }, [view, getPostNavigationOptions]);

  // Handle any potential post-navigation navigation.
  useLayoutEffect(() => {
    const { internalUrl } = getPostNavigationOptions();
    if (!internalUrl) return;
    clearPostNavigationOptions();

    const parsed = parseUri(internalUrl);
    const isAbsoluteAppLink =
      (parsed?.protocol === 'https' || parsed?.protocol === 'http') &&
      parsed?.path?.includes('/app-link/');

    if (isAbsoluteAppLink) {
      redirectToPath(internalUrl);
    } else {
      handleInternalUrl(internalUrl);
    }
  }, [
    view,
    handleInternalUrl,
    redirectToPath,
    getPostNavigationOptions,
    clearPostNavigationOptions,
  ]);

  // Delay rendering the children until after the frame has rendered,
  // as content is using the header and footer elements.
  useEffect(() => setComponentHasRendered(true), []);

  useEffect(() => {
    if (
      componentHasRendered &&
      headerElRef.current &&
      mainElRef.current &&
      footerElRef.current
    ) {
      setHeaderElement(headerElRef.current);
      setMainElement(mainElRef.current);
      setFooterElement(footerElRef.current);
    }
  }, [
    componentHasRendered,
    setHeaderElement,
    setMainElement,
    setFooterElement,
  ]);

  // Focus the content wrapper whenever the view changes.
  useEffect(() => {
    headerElRef.current?.focus();
  }, [currentView]);

  const HEADER_STYLE: CSSObject = {
    width: '100%',
    zIndex: layout.HEADER_ZINDEX,
    // Adding the !important flag as this element needs to cary a pseudo-focus.
    // An inset box-shadow is used to draw the focus border globally, hence we want to override it here.
    boxShadow: 'none !important',

    [`@media (max-width: ${layout.BREAKPOINT_MEDIUM}px)`]: {
      position: 'sticky',
      top: getPartnerHeaderHeight(),
    },
  };

  const CONTENT_PORTAL_STYLE: CSSObject = {
    position: 'relative',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    backgroundColor: color.LOCAL_WHITE,

    [`@media (min-width: ${layout.BREAKPOINT_MEDIUM}px)`]: {
      marginTop: 0,
      overflowY: 'auto',
    },
  };

  const FOOTER_STYLE: CSSObject = {
    display: 'flex',
    flexDirection: 'column',
    zIndex: layout.FOOTER_ZINDEX,

    [`@media (max-width: ${layout.BREAKPOINT_MEDIUM}px)`]: {
      position: 'sticky',
      bottom: 0,
    },
  };

  return (
    <Fragment>
      {/* Header portal */}
      <header
        css={HEADER_STYLE}
        tabIndex={-1}
        ref={(ref) => (headerElRef.current = ref)}
      />

      {/* Content portal */}
      <main
        role='main'
        css={CONTENT_PORTAL_STYLE}
        ref={(ref) => (mainElRef.current = ref)}
      >
        <Suspense
          fallback={
            <div role='status' aria-label='Loading'>
              <Loader show />
            </div>
          }
        >
          {componentHasRendered && getViewComponent()}
        </Suspense>
      </main>

      {/* Footer portal - used by the BottomSticky component for output */}
      <footer css={FOOTER_STYLE} ref={(ref) => (footerElRef.current = ref)} />
    </Fragment>
  );
});

export default View;
